import React from 'react';
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
} from '@mui/material';
import { Receipt } from '../../../pages/Billing/Invoice/invoiceHelper';
import { ReportFooterTimestamp } from '../../../utils/reportUtils';
import useCurrency from '../../CurrencyProvider';
import { ClinicHeader } from '../ClinicHeader';
import PatientDetailsHeader from '../PatientDetailsHeader';

type Props = {
  receiptData: Receipt;
};

const ReceiptReport: React.FC<Props> = ({ receiptData }) => {
  const { formatCurrency } = useCurrency();

  const {
    receipt_no,
    created_by,
    print_date_time,
    total_bill_amount,
    total_received_amount,
    payment_summary,
    invoice_details,
  } = receiptData;

  return (
    <Box
      sx={{
        p: 4,
        backgroundColor: '#fff',
        color: '#333',
        fontFamily: '"Segoe UI", Arial, sans-serif',
        mx: 'auto',
      }}
    >
      {/* Clinic Header */}
      <ClinicHeader />

      {/* Patient Details Header */}
      <PatientDetailsHeader />


      {/* Receipt Title with decorative lines */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          my: 3,
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to right, transparent, #e0e0e0)' }} />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 2,
            color: '#333',
          }}
        >
          Payment Receipt
        </Typography>
        <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to left, transparent, #e0e0e0)' }} />
      </Box>

      {/* Receipt Info Card */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          backgroundColor: '#fafafa',
          border: '1px dashed #ddd',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography variant="caption" sx={{ color: '#888', textTransform: 'uppercase', fontSize: 10 }}>
              Receipt No.
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, color: '#333', fontFamily: 'monospace' }}>
              {receipt_no}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: '#888', textTransform: 'uppercase', fontSize: 10 }}>
              Bill No.
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#555' }}>
              {invoice_details.bill_no || 'N/A'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#888', textTransform: 'uppercase', fontSize: 10 }}>
              Date & Time
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#555' }}>
              {print_date_time}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: '#888', textTransform: 'uppercase', fontSize: 10 }}>
              Received By
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#555' }}>
              {created_by}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Dotted Divider */}
      <Box
        sx={{
          borderBottom: '2px dotted #ccc',
          my: 2,
        }}
      />

      {/* Payment Summary */}
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1.5,
          color: '#666',
          textTransform: 'uppercase',
          letterSpacing: 1,
          fontSize: 12,
        }}
      >
        Payment Details
      </Typography>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid #eee',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8f8f8' }}>
              <TableCell sx={{ fontWeight: 600, color: '#555', py: 1.5, width: 60 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#555', py: 1.5 }}>Payment Mode</TableCell>
              <TableCell sx={{ fontWeight: 600, color: '#555', py: 1.5 }} align="right">
                Amount
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payment_summary.map((item, index) => (
              <TableRow
                key={index}
                sx={{
                  '&:last-child td': { borderBottom: 0 },
                  '&:nth-of-type(even)': { backgroundColor: '#fcfcfc' },
                }}
              >
                <TableCell sx={{ py: 1.5, color: '#888' }}>{index + 1}</TableCell>
                <TableCell sx={{ py: 1.5, fontWeight: 500 }}>{item.payment_mode_name}</TableCell>
                <TableCell align="right" sx={{ py: 1.5, fontWeight: 600, fontFamily: 'monospace' }}>
                  {formatCurrency(item.amount)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Total Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
          borderRadius: 2,
          border: '1px solid #c8e6c9',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: '#666', textTransform: 'uppercase', fontSize: 10 }}>
              Bill Amount
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
              {formatCurrency(parseFloat(total_bill_amount))}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ mx: 3 }} />
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: '#2e7d32', textTransform: 'uppercase', fontSize: 10, fontWeight: 600 }}>
              Total Received
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32' }}>
              {formatCurrency(total_received_amount)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Thank You Message */}
      <Box sx={{ textAlign: 'center', my: 3 }}>
        <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic' }}>
          Thank you for your payment!
        </Typography>
      </Box>


      {/* Decorative divider */}
      <Box
        sx={{
          borderBottom: '2px dashed #ccc',
          my: 2,
        }}
      />

      <ReportFooterTimestamp />
    </Box>
  );
};

export default ReceiptReport;
